import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { ROUTES, NAVIGATION_OPTIONS } from '../config/routes';
import { 
  newPasswordFormStyles, 
  newPasswordFormAnimations, 
  newPasswordFormUtils,
  COLORS
} from '../styles/components/NewPasswordForm.styles';
import PasswordChangeSuccessModal from '../components/ui/PasswordChangeSuccessModal';
import { 
  validatePassword, 
  validatePasswordConfirm, 
  checkPasswordStrength
} from '../utils/formValidation';
import { getEnvironmentConfig } from '../config/apiEndpoints';

/**
 * NewPasswordFormコンポーネント
 * パスワードリセット用の新しいパスワード設定フォーム
 * バックエンドAPIのパスワード変更エンドポイントと連携
 */
const NewPasswordForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // URLパラメータから取得
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  
  // フォーム状態
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  
  // エラー・ローディング状態
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [isValidCode, setIsValidCode] = useState(false);

  // モーダル制御用の state
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // API設定
  const config = getEnvironmentConfig();

  /**
   * コンポーネント初期化時にリセットコードの検証
   */
  useEffect(() => {
    // CSSアニメーションを挿入
    if (!document.getElementById('new-password-form-animations')) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'new-password-form-animations';
      styleSheet.textContent = newPasswordFormAnimations;
      document.head.appendChild(styleSheet);
    }

    /**
     * バックエンドAPI リセットコード検証処理
     */
    const verifyResetCode = async () => {
      if (!oobCode || mode !== 'resetPassword') {
        setErrors({ general: '無効なリンクです' });
        setVerifying(false);
        return;
      }

      try {
        // バックエンドAPIでリセットコードを検証
        const response = await fetch(`${config.baseUrl}/api/auth/verify-reset-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ oobCode })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || '無効なリセットコードです');
        }

        setEmail(data.email);
        setIsValidCode(true);
      } catch (error) {
        console.error('[NEW PASSWORD] Invalid reset code:', error);
        setErrors({ general: error.message || '無効または期限切れのリンクです' });
        setIsValidCode(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyResetCode();
  }, [oobCode, mode, config.baseUrl]);

  /**
   * 強いパスワードのバリデーション
   */
  const passwordValidationOptions = {
    minLength: 8,
    requireStrong: true
  };

  /**
   * パスワード入力時のハンドラー
   */
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    const passwordError = validatePassword(newPassword, passwordValidationOptions);
    setErrors(prev => ({ ...prev, password: passwordError }));

    if (confirmPassword) {
      const confirmError = validatePasswordConfirm(newPassword, confirmPassword);
      setErrors(prev => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  /**
   * パスワード確認入力時のハンドラー
   */
  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    
    // パスワード一致チェック（両方に値がある場合のみ）
    if (password && newConfirmPassword) {
      const confirmError = validatePasswordConfirm(password, newConfirmPassword);
      setErrors(prev => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  /**
   * フォーム送信時のハンドラー
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const passwordError = validatePassword(password, passwordValidationOptions);
    if (passwordError) {
      setErrors({ password: passwordError });
      return;
    }

    const confirmError = validatePasswordConfirm(password, confirmPassword);
    if (confirmError) {
      setErrors({ confirmPassword: confirmError });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // バックエンドAPIでパスワードをリセット
      const response = await fetch(`${config.baseUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          oobCode,
          newPassword: password
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'パスワードの変更に失敗しました');
      }
      setShowSuccessModal(true);
    } catch (error) {
      console.error('[NEW PASSWORD] Password reset failed:', error);
      setErrors({ general: error.message || 'パスワードの変更に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * 成功モーダル制御関数群
   */
  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  // ログインページに遷移
  const handleNavigateToLogin = () => {
    setShowSuccessModal(false);
    navigate(ROUTES.LOGIN, NAVIGATION_OPTIONS.REPLACE);
  };

  /**
   * パスワード強度表示コンポーネント
   */
  const PasswordStrengthIndicator = ({ password }) => {
    if (!password) return null;
    
    const strengthInfo = checkPasswordStrength(password);
    const allRequirementsMet = strengthInfo.isValid;
    
    return (
      <div style={newPasswordFormUtils.getStrengthIndicatorStyle(
        newPasswordFormStyles.strengthIndicator, 
        allRequirementsMet
      )}>
        <div style={newPasswordFormStyles.strengthTitle}>パスワード要件:</div>
        <div style={newPasswordFormStyles.strengthRequirements}>
          <div style={{
            ...newPasswordFormStyles.requirement,
            ...(strengthInfo.length ? newPasswordFormStyles.requirementMet : newPasswordFormStyles.requirementUnmet)
          }}>
            <span>{strengthInfo.length ? '✓' : '✗'}</span>
            <span>8文字以上</span>
          </div>
          <div style={{
            ...newPasswordFormStyles.requirement,
            ...(strengthInfo.uppercase ? newPasswordFormStyles.requirementMet : newPasswordFormStyles.requirementUnmet)
          }}>
            <span>{strengthInfo.uppercase ? '✓' : '✗'}</span>
            <span>英大文字を含む (A-Z)</span>
          </div>
          <div style={{
            ...newPasswordFormStyles.requirement,
            ...(strengthInfo.lowercase ? newPasswordFormStyles.requirementMet : newPasswordFormStyles.requirementUnmet)
          }}>
            <span>{strengthInfo.lowercase ? '✓' : '✗'}</span>
            <span>英小文字を含む (a-z)</span>
          </div>
          <div style={{
            ...newPasswordFormStyles.requirement,
            ...(strengthInfo.number ? newPasswordFormStyles.requirementMet : newPasswordFormStyles.requirementUnmet)
          }}>
            <span>{strengthInfo.number ? '✓' : '✗'}</span>
            <span>数字を含む (0-9)</span>
          </div>
        </div>
        
        <div style={newPasswordFormUtils.getStrengthSummaryStyle(
          newPasswordFormStyles.strengthSummary,
          allRequirementsMet
        )}>
          {allRequirementsMet ? '✅ すべての要件を満たしています' : '❌ 上記要件をすべて満たしてください'}
        </div>
      </div>
    );
  };

  // 検証中の表示
  if (verifying) {
    return (
      <div style={newPasswordFormStyles.pageContainer}>
        <div style={newPasswordFormStyles.container}>
          <div style={newPasswordFormStyles.loadingContainer}>
            <div style={newPasswordFormStyles.loadingSpinner}></div>
            <p>リセットコードを確認しています...</p>
          </div>
        </div>
      </div>
    );
  }

  // 無効なコードの場合
  if (!isValidCode) {
    return (
      <div style={newPasswordFormStyles.pageContainer}>
        <div style={newPasswordFormStyles.container}>
          <div style={newPasswordFormStyles.errorContainer}>
            <h2 style={newPasswordFormStyles.errorTitle}>エラー</h2>
            <p style={newPasswordFormStyles.description}>
              {errors.general || '無効または期限切れのリンクです'}
            </p>
            <button 
              onClick={() => navigate(ROUTES.LOGIN)}
              style={newPasswordFormStyles.submitButton}
            >
              ログインページに戻る
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={newPasswordFormStyles.pageContainer}>
      <div style={newPasswordFormStyles.container}>
        <h2 style={newPasswordFormStyles.title}>新しいパスワードの設定</h2>
        <p style={newPasswordFormStyles.description}>
          {`${email ?? ''} のパスワードを新しく設定してください`}
        </p>

        <form onSubmit={handleSubmit} style={newPasswordFormStyles.form}>
          {/* 新しいパスワード */}
          <div style={newPasswordFormStyles.fieldGroup}>
            <label htmlFor="password" style={newPasswordFormStyles.label}>
              <span>新しいパスワード</span><span style={newPasswordFormStyles.required}> *</span>
            </label>
            <div style={newPasswordFormStyles.passwordContainer}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={handlePasswordChange}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField('')}
                disabled={loading}
                style={newPasswordFormUtils.getInputStyle(
                  newPasswordFormStyles.input,
                  errors.password,
                  focusedField === 'password'
                )}
                required
                autoComplete="new-password"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={newPasswordFormStyles.passwordToggle}
                disabled={loading}
                onMouseEnter={(e) => {
                  if (!loading) {
                    Object.assign(e.target.style, newPasswordFormStyles.passwordToggleHover);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.color = COLORS.textGray;
                  }
                }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && (
              <div style={newPasswordFormStyles.errorMessage}>
                {errors.password}
              </div>
            )}
            
            <PasswordStrengthIndicator password={password} />
          </div>

          {/* パスワード確認 */}
          <div style={newPasswordFormStyles.fieldGroup}>
            <label htmlFor="confirmPassword" style={newPasswordFormStyles.label}>
              <span>パスワード確認</span><span style={newPasswordFormStyles.required}> *</span>
            </label>
            <div style={newPasswordFormStyles.passwordContainer}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField('')}
                disabled={loading}
                style={newPasswordFormUtils.getInputStyle(
                  newPasswordFormStyles.input,
                  errors.confirmPassword,
                  focusedField === 'confirmPassword'
                )}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={newPasswordFormStyles.passwordToggle}
                disabled={loading}
                onMouseEnter={(e) => {
                  if (!loading) {
                    Object.assign(e.target.style, newPasswordFormStyles.passwordToggleHover);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.color = COLORS.textGray;
                  }
                }}
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <div style={newPasswordFormStyles.errorMessage}>
                {errors.confirmPassword}
              </div>
            )}
          </div>

          {/* 全般エラー */}
          {errors.general && (
            <div style={newPasswordFormStyles.generalError}>
              <div style={newPasswordFormStyles.errorMessage}>
                {errors.general}
              </div>
            </div>
          )}

          {/* 送信ボタン */}
          <button 
            type="submit" 
            disabled={loading || errors.password || errors.confirmPassword || !password || !confirmPassword}
            style={newPasswordFormUtils.getButtonStyle(
              newPasswordFormStyles.submitButton,
              loading || errors.password || errors.confirmPassword || !password || !confirmPassword,
              loading
            )}
            onMouseEnter={(e) => {
              if (!loading && !errors.password && !errors.confirmPassword && password && confirmPassword) {
                Object.assign(e.currentTarget.style, newPasswordFormStyles.submitButtonHover);
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && !errors.password && !errors.confirmPassword && password && confirmPassword) {
                e.currentTarget.style.backgroundColor = COLORS.primary;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            {loading ? (
              <>
                <div style={newPasswordFormStyles.loadingSpinner}></div>
                <span>変更中...</span>
              </>
            ) : (
              <span>パスワードを変更</span>
            )}
          </button>
        </form>

        {/* ログインページに戻るリンク */}
        <div style={{ textAlign: 'center' }}>
          <button 
            type="button"
            onClick={() => navigate(ROUTES.LOGIN)}
            style={newPasswordFormStyles.link}
            onMouseEnter={(e) => {
              Object.assign(e.target.style, newPasswordFormStyles.linkHover);
            }}
            onMouseLeave={(e) => {
              e.target.style.color = COLORS.primary;
              e.target.style.textDecoration = 'none';
            }}
          >
            ← ログインページに戻る
          </button>
        </div>

       {/* パスワード変更成功モーダル */}
        <PasswordChangeSuccessModal
          isOpen={showSuccessModal}
          onClose={handleCloseModal}
          onNavigateToLogin={handleNavigateToLogin}
        />
      </div>
    </div>
  );
};

export default NewPasswordForm;